import {
  type CompositionV2Filters,
  PositionsEntityKind,
} from '@revelio/data-access';

import type { AllSelectionListsWithSkillTaxonomyKey } from '../../../../shared/selection-lists/types';
import { DEFAULT_SKILL_TAXONOMY_ID } from '../hooks/useAdaptiveSkillTaxonomy';
import {
  type AllSelectionListKeys,
  type Filter,
  FilterId,
  FilterType,
  type PrimaryFilterId,
} from '../types/filters';
import { getCustomRoleFilter } from './getCustomRoleFilter';
import { getCustomSkillFilter } from './getCustomSkillFilter';
import { getDateFromFilters } from './getDateFilter';
import { getListFilter } from './getListFilter';
import { getStaticListBoolean } from './getStaticListBoolean';
import { getV2CompanyFilters } from './getV2CompanyFilters';
import { getV2GeographyFilters } from './getV2GeographyFilters';
import { stripVersionFromTaxonomyId } from './taxonomyHelpers';

export const PositionsEntityToLevelId: Record<
  PositionsEntityKind,
  AllSelectionListKeys
> = {
  [PositionsEntityKind.RicsK10]: 'rics_k10',
  [PositionsEntityKind.RicsK50]: 'rics_k50',
  [PositionsEntityKind.RicsK400]: 'rics_k400',
  [PositionsEntityKind.Company]: 'company',
  [PositionsEntityKind.Region]: 'region',
  [PositionsEntityKind.Country]: 'country',
  [PositionsEntityKind.Msa]: 'msa',
  [PositionsEntityKind.State]: 'state' as AllSelectionListKeys,
  [PositionsEntityKind.City]: 'city' as AllSelectionListKeys,
  [PositionsEntityKind.RoleK7]: 'role_k7',
  [PositionsEntityKind.RoleK150]: 'role_k150',
  [PositionsEntityKind.RoleK1500]: 'role_k1500',
  [PositionsEntityKind.RoleK15000]: 'role_k15000',
};

export const positionsEntityLookup: Partial<
  Record<AllSelectionListsWithSkillTaxonomyKey, PositionsEntityKind>
> = Object.fromEntries(
  Object.entries(PositionsEntityToLevelId).map(([key, value]) => [
    value,
    key as PositionsEntityKind,
  ])
);

export const V2DateGranularity = 'month' as const;

export const getV2Filters = ({
  primaryDimension,
  isSnapshot = false,
  effectiveFilters,
}: {
  primaryDimension: PrimaryFilterId;
  isSnapshot?: boolean;
  effectiveFilters: Filter[];
}): CompositionV2Filters => {
  const v2Filters = effectiveFilters.reduce<CompositionV2Filters>(
    (acc, filter): CompositionV2Filters => {
      if (filter.id === primaryDimension) {
        return acc;
      }

      const getV2ListFilter = (key: keyof CompositionV2Filters) => {
        if (filter.type !== FilterType.LIST) return acc;
        return {
          ...acc,
          ...getListFilter<CompositionV2Filters>(filter, key),
        };
      };

      return ((): CompositionV2Filters => {
        switch (filter.id) {
          case FilterId.COMPANY:
            return { ...acc, ...getV2CompanyFilters(filter) };

          case FilterId.GEOGRAPHY:
            return { ...acc, ...getV2GeographyFilters(filter) };

          case FilterId.ROLE:
            return { ...acc, ...getCustomRoleFilter(filter, effectiveFilters) };

          case FilterId.CUSTOM_SKILL:
            return {
              ...acc,
              ...getCustomSkillFilter(filter, effectiveFilters),
            };

          case FilterId.SKILL_TAXONOMY: {
            if (filter.type === FilterType.VALUE && filter.value) {
              const taxonomyId =
                (filter.value as { taxonomyId?: number })?.taxonomyId ??
                DEFAULT_SKILL_TAXONOMY_ID;
              return {
                ...acc,
                custom_skill: {
                  ...(acc.custom_skill || {}),
                  taxonomy_id: taxonomyId,
                },
              };
            }
            return acc;
          }

          case FilterId.GENDER:
            return getV2ListFilter('gender');

          case FilterId.ETHNICITY:
            return getV2ListFilter('ethnicity');

          case FilterId.EDUCATION:
            return getV2ListFilter('highest_degree');

          case FilterId.SENIORITY:
            return getV2ListFilter('seniority');

          case FilterId.ROLE_TAXONOMY: {
            if (filter.type === FilterType.VALUE && filter.value) {
              const customRoleAcc = acc.custom_role || {};
              return {
                ...acc,
                custom_role: {
                  ...customRoleAcc,
                  taxonomyId: stripVersionFromTaxonomyId(filter.value),
                },
              };
            }
            return acc;
          }

          case FilterId.TITLE: {
            if (filter.type === FilterType.FREE_TEXT) {
              return { ...acc, raw_title: [filter.text] };
            }
            return acc;
          }

          case FilterId.KEYWORDS: {
            if (filter.type === FilterType.FREE_TEXT) {
              return { ...acc, keywords: [filter.text] };
            }
            return acc;
          }

          case FilterId.DATA_ADJUSTMENTS:
            return {
              ...acc,
              ...getStaticListBoolean(filter, 'raw'),
            };

          case FilterId.CONTINGENT_WORKERS:
            return {
              ...acc,
              ...getStaticListBoolean(filter, 'contingent_workers'),
            };

          // biome-ignore lint/complexity/noUselessSwitchCase: migrated from develop
          case FilterId.DATE_RANGE:
          // biome-ignore lint/complexity/noUselessSwitchCase: migrated from develop
          case FilterId.SNAPSHOT_DATE:
          // biome-ignore lint/complexity/noUselessSwitchCase: migrated from develop
          case FilterId.OVERALL_RATING:
          // biome-ignore lint/complexity/noUselessSwitchCase: migrated from develop
          case FilterId.POSTINGS_SOURCE:
          default:
            return acc;
        }
      })();
    },
    { raw: false }
  );

  const dateFilter = getDateFromFilters({
    filters: effectiveFilters,
    granularity: V2DateGranularity,
    isSnapshot,
  });

  return { ...v2Filters, ...dateFilter };
};
